import React, { useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { i18nIntl, isNotEmpty } from '../../../lib/ep';
import { useCSS } from '../../../lib/hooks';

import PrizePurchaseLayer from './PrizePurchaseLayer';
import FloatingLayerPublic from '../FloatingLayerPublic';
import ActionButton from '../../builder/ActionButton';
import TokenAmount, { formatTokenTitle } from './TokenAmount';

const DisabledRibbonWrapper = (props) => {
	return (
		<div class="disabled_ribbon">
			{props.children}
		</div>
	)
};

const DisabledRibbon = ({ reason, token, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	if(reason == 'not_more_units') {
		return (
			<DisabledRibbonWrapper>
				{i18n("Sold out")}
			</DisabledRibbonWrapper>
		)
	}

	if(reason == 'already_purchased') {
		return (
			<DisabledRibbonWrapper>
				{i18n("Redeemed")}
			</DisabledRibbonWrapper>
		)
	}

	if(reason == 'not_enough_tokens') {
		return (
			<DisabledRibbonWrapper>
				{props.intl.formatMessage({
					id:"{num} {coin_name} missing",
				}, {
					num:props.missing_tokens,
					coin_name:formatTokenTitle(token, props.missing_tokens),
				})}
			</DisabledRibbonWrapper>
		)
	}

	return null;
};

const SinglePrizeItemWrapper = (props) => {
	const is_enabled = props?.is_enabled ?? false;

	const classes = ['single_entry_in_gallery', 'original_color_item'];
	if(!is_enabled) classes.push('is_disabled');

	return (
		<li class={classes.join(" ")} onClick={(is_enabled) ? props.onClick : null}>
			<div class="gui_single_entry_contents">
				<div class="main_c">
					<div class="c">
						{props.children}
					</div>
				</div>
			</div>
		</li>
	)
};

const SinglePrizeItem = ({ prize, token, ...props }) => {
	const prize_price = prize.loyalty_price[0];
	const is_enabled = props?.is_enabled ?? false;
	const prize_status = props?.prize_status ?? {};
	const display_prize_description = props.promotion?.loyalty_display_prize_desc ?? true;
	const loyalty_display_exhausted = props.promotion?.loyalty_display_exhausted ?? true;
	const loyalty_display_units = props.promotion?.loyalty_display_units ?? false;
	if(!is_enabled && prize_status.data.reason == 'not_more_units' && !loyalty_display_exhausted) {
		return null;
	}

	return (
		<SinglePrizeItemWrapper
			is_enabled={is_enabled}
			onClick={props.onClick}
		>
			<div class="entry_gallery_container gui_pos_rel">
				<div class="entry_gallery">
					<img src="/img/blank_120x63.png" width="100%" alt="" />

					<ul class="gui_media_gallery is_l has_1">
						<li class="">
							<div class="photo_embed" style={{ aspectRatio:'120 / 63', height:'unset' }}>
								<img
									src={(isNotEmpty(prize, 'full_image')) ? prize.full_image : `${window.cdn_url}img/default_prize_img.png`}
									height="100%"
									width="100%"
									alt={prize.title}
								/>
							</div>
						</li>
					</ul>
				</div>

				{(!is_enabled) && (
					<DisabledRibbon
						{...prize_status.data}
						token={token}
						intl={props.intl}
					/>
				)}
			</div>

			<div class="gui_entry_info">
				<div class="participation_info">
					<div class="participant_data">
						<div class="main_user_info">
							<strong class="user_name mod_align_left">{prize.title}</strong>

							{(display_prize_description && isNotEmpty(prize, 'description')) && (
								<div class="short_description">{prize.description}</div>
							)}

							{loyalty_display_units && (
								<div class="short_description">
									<FormattedMessage id="{units} units left" values={{ units:prize.remaining }} />	
								</div>
							)}
						</div>
					</div>
				</div>

				<div class="footer_container">
					<div class="participation_footer original_color_item">
						<div class="participation_actions">
							<TokenAmount
								token={token}
								amount={prize_price.amount}
							/>

							<div class="flex_button in_top adaptable_button gui_actions">
								<ActionButton
									_fullclass='vote_button'
								>Redeem</ActionButton>
							</div>
						</div>
					</div>
				</div>
			</div>
		</SinglePrizeItemWrapper>
	)
};

const PrizeShop = ({ items, stage, tokens, ...props }) => {
	useCSS('comp/multigallery.css');

	const [ display_claim_confirm, setDisplayClaimConfirm ] = useState(false);
	const [ prize, setPrize ] = useState(false);
	const [ purchase_error, setPurchaseError ] = useState(false);

	const is_logged = props?.is_logged ?? false;

	const promotion = props?.promotion ?? false;
	
	if(items.length == 0) return null;
	if(promotion === false) return null;
	if(Object.keys(tokens).length == 0) return null;

	const user_tokens = props?.user_tokens ?? {};
	const is_builder = props?.is_builder ?? false;

	const openClaimConfirmLayer = (prize) => {
		if(is_builder) return;

		setDisplayClaimConfirm(true);
		setPrize(prize);
	};

	const claim = (id, qty = 1) => {
		if(is_builder) return;

		if(props.onClaim != undefined) props.onClaim(id, qty, (res) => {
			setPurchaseError(res);
			return;
		});
	};

	const filtered_items = items.filter((p) => (p?.loyalty_enabled == 1)).sort((a, b) => {
		if(parseInt(a.loyalty_price[0].amount) > parseInt(b.loyalty_price[0].amount)) return 1;
		if(parseInt(a.loyalty_price[0].amount) < parseInt(b.loyalty_price[0].amount)) return -1;

		if(parseInt(a.loyalty_price[0].token) > parseInt(b.loyalty_price[0].token)) return -1;
		if(parseInt(a.loyalty_price[0].token) < parseInt(b.loyalty_price[0].token)) return 1;

		return 0;
	});

	const checkIfAvailablePrize = (user_tokens, prize_price, prize) => {
		if(prize.status == '3') {
			return {
				enabled:false,
				data:{
					reason:'already_purchased',
				}
			};
		}

		if(prize.status == '2') {
			return {
				enabled:false,
				data:{
					reason:'not_more_units',
				}
			};
		}

		const missing_tokens = user_tokens - parseInt(prize_price.amount);

		if(missing_tokens < 0) {
			return {
				enabled:false,
				data:{
					reason:'not_enough_tokens',
					missing_tokens:-missing_tokens,
				}
			};
		}

		return {
			enabled:true,
		};
	};

	return (
		<>
			<ul class="prize_gallery gui_multimedia_gallery has_column_of_4 gui_main_text_color_on_bg gui_pos_rel">
				{filtered_items.map((prize) => {
					if(prize?.loyalty_price == undefined) return null;

					const prize_price = prize.loyalty_price[0];

					const token = tokens[prize_price.token];
					if(token == undefined) return null;

					const user_price_token = (user_tokens != undefined && user_tokens[token.id] != undefined) ? parseInt(user_tokens[token.id]) : 0;

					const prize_status = checkIfAvailablePrize(user_price_token, prize_price, prize);

					let is_enabled = prize_status?.enabled ?? false;
					if(is_builder) is_enabled = false;
					if(!is_logged) is_enabled = true;

					return (
						<SinglePrizeItem
							key={`prize_${prize.id}`}
							prize={prize}
							token={token}
							is_enabled={is_enabled}
							prize_status={prize_status}
							promotion={promotion}
							onClick={(is_enabled && !is_builder) ? () => {
								if(!is_logged) {
									window.location.href = `/login/${promotion.id}`;
									return;
								}

								openClaimConfirmLayer(prize);
							} : null}
							intl={props.intl}
						/>
					)
				})}
			</ul>

			{(display_claim_confirm !== false) && (
				<FloatingLayerPublic
					backButton={() => setDisplayClaimConfirm(false)}
					is_builder={is_builder}
					enable_behavior={true}
					content_styles={{ maxHeight:'66svh' }}
				>
					<PrizePurchaseLayer
						prize={prize}
						tokens={tokens}
						user_tokens={user_tokens}
						promotion={promotion}
						backButton={() => setDisplayClaimConfirm(false)}
						claim={claim}
						is_builder={is_builder}
						intl={props.intl}
					/>
				</FloatingLayerPublic>
			)}
		</>
	)
};

export default injectIntl(PrizeShop);